import React, { useState } from 'react';
import { X, Send, AlertTriangle, Shield, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

const SiteTransferModal = ({ site, isOpen, onClose, onTransferComplete }) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [transferMessage, setTransferMessage] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferPreview, setTransferPreview] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handlePreviewTransfer = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter recipient email');
      return;
    }

    try {
      // For now, we'll just set a preview - in a real app you might want a separate preview endpoint
      setTransferPreview({
        recipientEmail: recipientEmail.trim(),
        message: transferMessage.trim()
      });
      setShowConfirmation(true);
    } catch (error) {
      toast.error('Failed to prepare transfer preview');
    }
  };

  const handleTransfer = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter recipient email');
      return;
    }

    setIsTransferring(true);

    try {
      const response = await fetch(`/api/sites/${site._id}/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipientEmail: recipientEmail.trim(),
          transferMessage: transferMessage.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Site transferred successfully to ${data.recipient.email}`);
        
        // Show transfer details if there are restrictions
        if (data.restrictedAccess && data.restrictions.length > 0) {
          toast((t) => (
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-yellow-500" />
                <span className="font-medium">Transfer Complete with Restrictions</span>
              </div>
              <div className="text-sm text-gray-600">
                The recipient can publish but has limited editing due to their plan.
              </div>
              <div className="text-xs text-gray-500">
                Restrictions: {data.restrictions.map(r => r.feature).join(', ')}
              </div>
            </div>
          ), { duration: 8000 });
        }
        
        onTransferComplete && onTransferComplete(data);
        handleClose();
      } else {
        toast.error(data.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error('Failed to transfer site');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleClose = () => {
    if (!isTransferring) {
      setRecipientEmail('');
      setTransferMessage('');
      setTransferPreview(null);
      setShowConfirmation(false);
      onClose();
    }
  };

  if (!isOpen || !site) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Transfer Site</h2>
            <p className="text-sm text-gray-600 mt-1">"{site.name}"</p>
          </div>
          <button
            onClick={handleClose}
            disabled={isTransferring}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {!showConfirmation ? (
            <>
              {/* Transfer Form */}
              <div className="space-y-4">
                {/* Recipient Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Enter recipient's email address"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isTransferring}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    The recipient must have an existing account with this email
                  </p>
                </div>

                {/* Transfer Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transfer Message (Optional)
                  </label>
                  <textarea
                    value={transferMessage}
                    onChange={(e) => setTransferMessage(e.target.value)}
                    placeholder="Add a message for the recipient..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    disabled={isTransferring}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {transferMessage.length}/500 characters
                  </p>
                </div>

                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Important Notes</h4>
                      <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                        <li>• Once transferred, you will lose all access to this site</li>
                        <li>• The recipient will become the new owner</li>
                        <li>• If recipient's plan has limitations, they may have restricted editing</li>
                        <li>• This action cannot be undone</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={handleClose}
                  disabled={isTransferring}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePreviewTransfer}
                  disabled={isTransferring || !recipientEmail.trim()}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Review Transfer</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Confirmation Step */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Site Transfer</h3>
                  <p className="text-gray-600">
                    Are you sure you want to transfer "{site.name}" to:
                  </p>
                </div>

                {/* Transfer Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Recipient:</span>
                      <span className="text-sm text-gray-900 ml-2">{transferPreview.recipientEmail}</span>
                    </div>
                    {transferPreview.message && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Message:</span>
                        <p className="text-sm text-gray-900 mt-1 bg-white p-2 rounded border">
                          {transferPreview.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Warning */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Final Warning</h4>
                      <p className="text-sm text-red-700 mt-1">
                        This will permanently transfer ownership. You will immediately lose access to this site.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowConfirmation(false)}
                  disabled={isTransferring}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={isTransferring}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isTransferring ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Transferring...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Transfer Site</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteTransferModal;